import { Injectable } from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Observable, of, shareReplay } from 'rxjs';
import { ExternalAudioValidationService } from '../../../services/external-audio-validation.service';
import { ExternalCallDurationValidationService } from '../../../services/external-call-duration-validation.service';
import { ExternalTranscriptValidationService } from '../../../services/external-transcript-validation.service';
import {
  AddRespondentRowFormControls,
  AddRespondentRowFormGroup,
  AddRespondentRowFormValue,
  AddRespondentRowsFormControls,
  RowMatchStatus,
} from '../models/add-respondent.types';

type DurationValidationCacheEntry = {
  m4aFile: File;
  transcriptFile: File;
  validation$: Observable<ValidationErrors | null>;
};

@Injectable({ providedIn: 'root' })
export class AddRespondentRowFormService {
  readonly defaultMatchMessage = 'Assign files manually or stage them in bulk.';
  private durationValidationCache = new WeakMap<
    AddRespondentRowFormGroup,
    DurationValidationCacheEntry
  >();

  constructor(
    private externalTranscriptValidationService: ExternalTranscriptValidationService,
    private externalAudioValidationService: ExternalAudioValidationService,
    private externalCallDurationValidationService: ExternalCallDurationValidationService
  ) {}

  create(initial: Partial<AddRespondentRowFormValue> = {}) {
    return new FormGroup<AddRespondentRowFormControls>(
      {
        firstName: new FormControl(initial.firstName || '', {
          nonNullable: true,
          validators: [Validators.required],
        }),
        lastName: new FormControl(initial.lastName || '', {
          nonNullable: true,
          validators: [Validators.required],
        }),
        segment: new FormControl(initial.segment || '', {
          nonNullable: true,
          validators: [Validators.required],
        }),
        dateOfCall: new FormControl(initial.dateOfCall || '', {
          nonNullable: true,
          validators: [Validators.required],
        }),
        source: new FormControl(initial.source || '', {
          nonNullable: true,
          validators: [Validators.required],
        }),
        m4aFile: new FormControl<File | null>(initial.m4aFile || null, {
          asyncValidators: this.m4aValidator.bind(this),
        }),
        transcriptFile: new FormControl<File | null>(
          initial.transcriptFile || null,
          { asyncValidators: this.transcriptValidator.bind(this) }
        ),
        confirmed: new FormControl(initial.confirmed ?? true, {
          nonNullable: true,
        }),
        requiresReview: new FormControl(initial.requiresReview ?? false, {
          nonNullable: true,
        }),
        matchStatus: new FormControl<RowMatchStatus>(
          initial.matchStatus || 'manual',
          { nonNullable: true }
        ),
        matchMessage: new FormControl(
          initial.matchMessage || this.defaultMatchMessage,
          { nonNullable: true }
        ),
      },
      {
        validators: this.atLeastOneFileValidator,
        asyncValidators: this.audioDurationValidator.bind(this),
      }
    );
  }

  createRowsFormGroup(rows: AddRespondentRowFormGroup[] = []) {
    return new FormGroup<AddRespondentRowsFormControls>({
      respondents: new FormArray<AddRespondentRowFormGroup>(rows),
    });
  }

  applyAudioFile(row: AddRespondentRowFormGroup, file: File) {
    const m4aFileControl = row.controls.m4aFile;

    if (!this.isSupportedAudioSelection(file)) {
      m4aFileControl.setValue(null);
      this.updateManualRowState(row);
      m4aFileControl.setErrors({ unknownFormat: true });
      return;
    }

    m4aFileControl.setValue(file);
    this.updateManualRowState(row);
  }

  applyTranscriptFile(row: AddRespondentRowFormGroup, file: File) {
    const transcriptFileControl = row.controls.transcriptFile;

    if (!this.isTranscriptFile(file)) {
      transcriptFileControl.setValue(null);
      this.updateManualRowState(row);
      transcriptFileControl.setErrors({ unknownFormat: true });
      return;
    }

    transcriptFileControl.setValue(file);
    this.updateManualRowState(row);
  }

  removeAudioFile(row: AddRespondentRowFormGroup) {
    const m4aFileControl = row.controls.m4aFile;

    m4aFileControl.setValue(null);
    m4aFileControl.setErrors(null);

    this.updateManualRowState(row);
  }

  removeTranscriptFile(row: AddRespondentRowFormGroup) {
    const transcriptFileControl = row.controls.transcriptFile;

    transcriptFileControl.setValue(null);
    transcriptFileControl.setErrors(null);

    this.updateManualRowState(row);
  }

  isAudioFile(file: File) {
    const extension = this.getFileExtension(file.name);

    return ['.m4a', '.mp3', '.mp4'].includes(extension);
  }

  isTranscriptFile(file: File) {
    const extension = this.getFileExtension(file.name);

    return extension === '.vtt' || file.type === 'text/vtt';
  }

  isRowBlank(row: AddRespondentRowFormGroup | null) {
    if (!row) {
      return true;
    }

    return !(
      row.controls.firstName.value ||
      row.controls.lastName.value ||
      row.controls.segment.value ||
      row.controls.dateOfCall.value ||
      row.controls.source.value ||
      row.controls.m4aFile.value ||
      row.controls.transcriptFile.value
    );
  }

  private transcriptValidator(control: FormControl<File | null>) {
    return control.value
      ? this.externalTranscriptValidationService.validate(control.value)
      : of(null);
  }

  private m4aValidator(control: FormControl<File | null>) {
    return control.value
      ? this.externalAudioValidationService.validate(control.value)
      : of(null);
  }

  private atLeastOneFileValidator(
    formGroup: AddRespondentRowFormGroup
  ): ValidationErrors | null {
    const audio = formGroup.controls.m4aFile.value;
    const transcript = formGroup.controls.transcriptFile.value;

    return audio || transcript ? null : { noFile: true };
  }

  private audioDurationValidator(formGroup: AddRespondentRowFormGroup) {
    const m4aFile = formGroup.controls.m4aFile.value;
    const transcriptFile = formGroup.controls.transcriptFile.value;

    if (!m4aFile || !transcriptFile) {
      this.durationValidationCache.delete(formGroup);
      return of(null);
    }

    const cachedValidation = this.durationValidationCache.get(formGroup);

    if (
      cachedValidation?.m4aFile === m4aFile &&
      cachedValidation.transcriptFile === transcriptFile
    ) {
      return cachedValidation.validation$;
    }

    const validation$ = this.externalCallDurationValidationService
      .validate(transcriptFile, m4aFile)
      .pipe(shareReplay({ bufferSize: 1, refCount: false }));

    this.durationValidationCache.set(formGroup, {
      m4aFile,
      transcriptFile,
      validation$,
    });

    return validation$;
  }

  private updateManualRowState(row: AddRespondentRowFormGroup) {
    const audioFile = row.controls.m4aFile.value;
    const transcriptFile = row.controls.transcriptFile.value;

    const { matchStatus, message } = this.getRowStatusAndMessage(
      audioFile,
      transcriptFile
    );

    row.patchValue({
      matchStatus,
      matchMessage: message,
      confirmed: row.controls.requiresReview.value ? false : true,
    });
  }

  private getRowStatusAndMessage(
    audioFile: File,
    transcriptFile: File
  ): {
    matchStatus: RowMatchStatus;
    message: string;
  } {
    const matchStatus = 'manual';
    const message = this.defaultMatchMessage;

    if (audioFile && transcriptFile) {
      return {
        matchStatus: 'manual',
        message: 'Files were assigned manually.',
      };
    }

    if (audioFile) {
      return {
        matchStatus: 'recordingOnly',
        message: 'Recording assigned manually. Add a transcript if available.',
      };
    }

    if (transcriptFile) {
      return {
        matchStatus: 'transcriptOnly',
        message: 'Transcript assigned manually. No recording will be uploaded.',
      };
    }

    return { matchStatus, message };
  }

  private isSupportedAudioSelection(file: File) {
    const validMimeTypes = [
      'audio/mp4',
      'audio/x-m4a',
      'audio/mpeg',
      'video/mp4',
    ];
    const validExtensions = ['.m4a', '.mp3', '.mp4'];
    const fileExtension = this.getFileExtension(file.name);

    return (
      validMimeTypes.includes(file.type) &&
      validExtensions.includes(fileExtension)
    );
  }

  private getFileExtension(fileName: string) {
    const extensionIndex = fileName.lastIndexOf('.');

    return extensionIndex >= 0
      ? fileName.slice(extensionIndex).toLowerCase()
      : '';
  }
}
